import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map } from 'rxjs';
import { AuthCookieService } from '../../core/services/auth-cookie/auth-cookie.service';
import { DataResponse } from '../../models/data-response';

@Injectable({
  providedIn: 'root',
})
export class UtilityService {
  private http = inject(HttpClient);
  private authCookieService = inject(AuthCookieService);
  standardHeader = new HttpHeaders({ 'X-CSRF': '1' });

  private get headers(): HttpHeaders {
    // TODO: come back here when implementating auth
    return this.standardHeader.append(
      'Authorization',
      'Bearer ' + this.authCookieService.accessToken,
    );
  }

  getRequest<T>(url: string, rawResponse = false) {
    return rawResponse
      ? this.http.get<T>(url, { headers: this.headers })
      : this.http
          .get<DataResponse<T>>(url, { headers: this.headers })
          .pipe(map((response) => response.result));
  }

  postRequest<T>(
    url: string,
    body: any,
    encode = false,
    withCredentials = false,
    rawResponse = false,
  ) {
    let header = this.headers;
    if (encode) header = header.append('Content-Type', 'application/x-www-form-urlencoded');

    return rawResponse
      ? this.http.post<T>(url, body, { headers: header, withCredentials })
      : this.http
          .post<DataResponse<T>>(url, body, { headers: header, withCredentials })
          .pipe(map((response) => response.result));
  }

  /**
   * Multipart POST surfaced as HttpEvents so the caller can report upload progress.
   * Content-Type is deliberately left unset — the browser has to add it together with the
   * multipart boundary, and setting it by hand produces an unparseable request.
   */
  postFileRequest<T>(url: string, formData: FormData) {
    return this.http.post<DataResponse<T>>(url, formData, {
      headers: this.headers,
      reportProgress: true,
      observe: 'events',
    });
  }

  deleteRequest<T>(url: string) {
    return this.http
      .delete<DataResponse<T>>(url, { headers: this.headers })
      .pipe(map((response) => response.result));
  }

  putRequest<T>(url: string, body: any, encode = false) {
    let header = this.headers;
    if (encode) header = header.append('Content-Type', 'application/x-www-form-urlencoded');

    return this.http
      .put<DataResponse<T>>(url, body, { headers: header })
      .pipe(map((response) => response.result));
  }

  patchRequest<T>(url: string, body: any = {}) {
    return this.http
      .patch<DataResponse<T>>(url, body, { headers: this.headers })
      .pipe(map((response) => response.result));
  }
}
